import * as Sentry from '@sentry/browser'
import { ExtraErrorData } from '@sentry/integrations'
import { Integrations } from '@sentry/tracing'
import { Extras } from '@sentry/types'
import * as R from 'ramda'

import { SENTRY_DNS } from '../constants/defaults'
import { Business, Nil, User } from '../types'
import { ApiError } from './errorTypes'
import getSessionId from './getSessionId'
import { getEnvironmentName, isLocalEnvironment, isProduction } from './index'

const RESPONSE_BODY_PAYLOAD_LIMIT = 2000
const ERROR_DATA_DEPTH = 5

const limitSize = (
  input: string | object,
  sizeLimit = RESPONSE_BODY_PAYLOAD_LIMIT,
) => {
  const inputString = R.is(String, input) ? input : JSON.stringify(input)
  return inputString.slice(0, sizeLimit)
}

export const captureSentryException = (
  error: ApiError,
  info?: Extras,
  user?: User,
  business?: Business,
  errorId?: string,
  logRocketUrl?: string,
) =>
  Sentry.withScope(scope => {
    if (business) {
      if (business.name) {
        scope.setTag('businessName', business.name)
      }
      if (business.id) {
        scope.setTag('businessId', business.id)
      }
    }
    if (user) {
      scope.setUser({
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
      })
    }
    if (errorId) {
      scope.setTag('errorId', errorId)
    }
    if (error.fingerprint) {
      scope.setFingerprint(error.fingerprint)
    }
    scope.setExtra('sessionId', getSessionId())
    scope.setTag('sessionId', getSessionId())
    if (error.arguments) {
      scope.setExtra('arguments', error.arguments)
    }
    if (error.responseBody) {
      scope.setExtra(
        'response',
        limitSize(error.responseBody, RESPONSE_BODY_PAYLOAD_LIMIT),
      )
    }
    if (error.status) {
      scope.setExtra('status', error.status)
    }
    if (error.payload) {
      scope.setExtra('payload', error.payload)
    }
    if (error.duration) {
      scope.setExtra('durationMs', error.duration)
    }
    if (logRocketUrl) {
      scope.setExtra('logRocketUrl', logRocketUrl)
    }
    if (info) {
      scope.setExtras(info)
    }

    scope.setExtra('online', navigator.onLine)

    return Sentry.captureException(error)
  })

export const initSentry = () => {
  if (isLocalEnvironment()) {
    return
  }
  Sentry.init({
    dsn: SENTRY_DNS,
    integrations: [
      new Integrations.BrowserTracing(),
      new ExtraErrorData({ depth: ERROR_DATA_DEPTH }),
    ],
    normalizeDepth: ERROR_DATA_DEPTH + 1,
    tracesSampleRate: isProduction() ? 0.6 : 0.05,
    release: process.env.REACT_APP_SENTRY_RELEASE,
    environment: getEnvironmentName(),
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
    ],
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === 'ui.click') {
        const { target } = hint?.event || {}
        if (target.innerText && breadcrumb.message !== 'body') {
          breadcrumb.message = `${breadcrumb.message}; Text: ${target.innerText}`
        }
      } else if (breadcrumb.category === 'xhr') {
        return breadcrumb?.data?.url.indexOf('api-js.mixpanel.com') > -1
          ? null
          : breadcrumb
      }

      return breadcrumb
    },
  })
}

export const setContext = (business: Business | Nil, user: User | Nil) => {
  if (business) {
    Sentry.setContext('business', {
      name: business?.name,
      id: business?.id,
    })
    Sentry.setTag('businessId', business.id)
  }
  if (user) {
    Sentry.setContext('user', {
      id: user.id,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
    })
    Sentry.setTag('userId', user.id)
  }
}
